import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { DataMainTemplate } from "@nocios/presentation-kit";
import Config from "src/Constants/Config";
import setCurrentPage from "src/Utils/setCurrentPage";
import Btn from "src/Component/Btn";

// Constants
const PAGE_ID = 1;
const BG_ID = 1;
const BG_TYPE = "jpg";

const LEFT_TITLE = "AI-First, Agile, Integrated\nIT and Network Operations";
const LEFT_DESCRIPTION = "Infosys Live Enterprise Automation Platform (LEAP)\n powered by BMC Helix AIOps";
const LEFT_DESCRIPTION_2 = "";
const LEFT_TEXT_1 = "Infosys LEAP is a Cloud enabled IT, Application &";
const LEFT_TEXT_2 =
  "Network management platform for enterprises\nempowering them to sense, analyze, decide, and\nproactively intervene to optimize operations and\nprevent disruptions.";

// Image slider component
const ImageSlider = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  useEffect(() => {
    setIsVisible(false); // Reset visibility when page changes
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [currentPage]); // This effect will run on currentPage changes

  const images = [1, 2, 3];
  const GAP = "calc(var(--width-73) * -1)";

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        top: "50%",
        right: 0,
        transform: "translateY(-50%)",
        width: "60%",
        height: "auto",
        pointerEvents: "none",
        zIndex: 10,
        overflow: "hidden",
      }}
    >
      {images.map((index, i) => (
        <div
          key={index}
          style={{
            marginTop: i === 0 ? "var(--width-50)" : 0,
            marginBottom: i < images.length - 1 ? GAP : 0,
            width: "100%",
            opacity: isVisible ? 1 : 0,
            transform: `translateX(${isVisible ? 0 : "100%"})`,
            transition: `opacity 0.7s ease-out, transform 0.7s ease-out`,
            transitionDelay: `${i * 0.3}s`,
          }}
        >
          <img
            src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/right_${index}.png`}
            alt={`Presentation Data ${index}`}
            style={{
              width: "75%",
              height: "auto",
              display: "block",
              animation: `glow 3s ease-in-out infinite`,
            }}
          />
        </div>
      ))}
    </div>
  );
};

// Main data function that returns the template data
const DATA = (): DataMainTemplate => {
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  const baseImageBG = `${Config.BASE_URL}/images/BG/`;

  // Navigation handler
  const handleButtonClick = (pageId: number | null) => {
    if (pageId !== null) {
      setCurrentPage(pageId);
    }
  };

  // Define keyframes for animations
  const keyframes = `
    @keyframes gradientMove {
      0% {
        background-position: 100% 50%;
      }
      100% {
        background-position: 0% 50%;
      }
    }

    @keyframes pulse {
      0% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.03);
      }
      100% {
        transform: scale(1);
      }
    }
    @keyframes shadowPulse {
      0% {
        box-shadow: 0 0 50px rgba(255,255,255, 0.3)
      }
      50% {
        box-shadow: 0 0 55px rgba(255,255,255,0.4);
      }
      100% {
        box-shadow: 0 0 50px rgba(255,255,255, 0.3);
      }
    }
    @keyframes glow {
      0% { filter: drop-shadow(0 0 1px rgba(125,208,255,0.1)); }
      50% { filter: drop-shadow(0 0 10px rgba(125,208,255,0.5)); }
      100% { filter: drop-shadow(0 0 1px rgba(125,208,255,0.1)); }
    }
  `;

  const leftAfterDescription = (
    <div style={{ padding: "var(--width-10) 0" }}>
      <style>{keyframes}</style>
      <img
        src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/left_separator.png`}
        alt="separator"
        style={{
          width: "var(--width-565)",
          height: "auto",
        }}
      />
      <div
        style={{
          color: "#fff",
          fontFamily: "Myriad Pro",
          fontSize: "var(--width-24)",
          fontStyle: "normal",
          fontWeight: "380",
          lineHeight: "150%",
          whiteSpace: "pre-line",
        }}
      >
        <p
          style={{
            marginTop: "var(--width-30)",
            marginBottom: 0,
          }}
        >
          {LEFT_TEXT_1}
        </p>
        <p
          style={{
            marginTop: "var(--width-0)",
          }}
        >
          {LEFT_TEXT_2}
        </p>
      </div>
      <Btn
        label="EXPLORE FURTHER"
        color="purple"
        onClick={() => handleButtonClick(2)}
        icon={
          <img
            src={`${Config.BASE_URL}/images/icons/product.png`}
            style={{
              width: "var(--width-28)",
              height: "auto",
            }}
            alt="product"
          />
        }
        style={{
          marginTop: "var(--width-65)",
          gap: "var(--width-6)",
          backgroundSize: "200% 100%",
          animation: "gradientMove 1.5s linear alternate-reverse infinite, pulse 3s linear infinite, shadowPulse 3s linear infinite",
        }}
      />
    </div>
  );

  return {
    bgVideo: {
      poster: `${baseImageBG}BG_${BG_ID}.${BG_TYPE}`,
      video: `${Config.BASE_URL}/videos/bg_${PAGE_ID}.mp4`,
    },
    gradient:
      "linear-gradient(270deg, rgba(21, 50, 121, 0.00) 11.77%, #153279 87.81%), linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%)",
    title: LEFT_TITLE,
    description: LEFT_DESCRIPTION + " " + LEFT_DESCRIPTION_2,
    leftAfterDescription: leftAfterDescription,
    // Force remount of ImageSlider on currentPage change by using key={currentPage}
    rightSideComponent: <ImageSlider key={currentPage} />,
  };
};

export default DATA;
